import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Modal,
  Form,
  ModalBody,
  Card,
  CardBody,
  CardHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import { useFormik } from "formik";
import { useSelector, useDispatch } from "react-redux";
import {
  addNewClient as onAddNewClient,
  updateClient,
} from "../../../slices/thunks";
import "react-toastify/dist/ReactToastify.css";
import { useProfile } from "../../../Components/Hooks/UserHooks";
import classnames from "classnames";
import NaturalClientForm from "./NaturalClientForm";
import LegalClientForm from "./LegalClientForm";
import { getClientValidationSchema } from "./ValidationSchemas";

const ClientModal = ({ show, clientOption, onCloseClick, extractedPersonalData = null }) => {
  const { clients } = useSelector((state) => {
    return state.Clients;
  });
  const { userProfile } = useProfile();

  const [activeArrowTab, setactiveArrowTab] = useState(1);
  const [passedarrowSteps, setPassedarrowSteps] = useState([1]);

  const isNaturalClient = clientOption === "NATURAL";

  const dispatch = useDispatch();

  function toggleArrowTab(tab) {
    if (activeArrowTab !== tab) {
      var modifiedSteps = [...passedarrowSteps, tab];

      if (tab >= 1 && tab <= 3) {
        setactiveArrowTab(tab);
        setPassedarrowSteps(modifiedSteps);
      }
    }
  }

  const handleCloseModal = () => {
    onCloseClick();
    setactiveArrowTab(1);
    validation.resetForm();
  };

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      buyer_type: clientOption,
      name: extractedPersonalData?.name || "",
      surname: extractedPersonalData?.surname || null,
      country: extractedPersonalData?.country || null,
      region: extractedPersonalData?.region || null,
      city: extractedPersonalData?.city || null,
      postal_code: null,
      address: extractedPersonalData?.address || null,
      profession: null,
      passport: null,
      social_security_number: extractedPersonalData?.social_security_number || null,
      social_security_series: extractedPersonalData?.social_security_series || null,
      mobile_phone_number: extractedPersonalData?.mobile_phone_number || "",
      land_phone_number: null,
      personal_email: extractedPersonalData?.personal_email || null,
      business_email: null,
      legal_company_name: null,
      legal_fiscal_code: null,
      legal_unique_registration_code: null,
      legal_trade_register_number: null,
      legal_vat_payer: true,
      legal_country: null,
      legal_region: null,
      legal_city: null,
      legal_office_address: null,
      iban_ron: "",
      iban_euro: "",
      add_second_client: false,
      additional_contact1: null,
      additional_contact2_id: null,
      additional_contact2_name: "",
      additional_contact2_surname: "",
      additional_contact2_ssn: "",
      additional_contact2_ssn_series: "",
      additional_contact2_mobile: "",
      additional_contact2_email: "",
      active: true,
    },
    validationSchema: getClientValidationSchema(
      activeArrowTab,
      isNaturalClient,
      clients
    ),

    onSubmit: (values) => {
      if (values.buyer_type === "NATURAL" && values.add_second_client) {
        const secondClient = {
          buyer_type: "NATURAL",
          name: values.additional_contact2_name,
          surname: values.additional_contact2_surname,
          social_security_number: values.additional_contact2_ssn,
          social_security_series: values.additional_contact2_ssn_series,
          mobile_phone_number: values.additional_contact2_mobile,
          personal_email: values.additional_contact2_email,
          country: values.country,
          region: values.region,
          city: values.city,
          postal_code: values.postal_code,
          address: values.address,
          active: true,
        };

        dispatch(onAddNewClient(secondClient))
          .then((secondClientResult) => {
            const secondClientId = secondClientResult.payload.id;

            const primaryClient = {
              buyer_type: values.buyer_type,
              name:
                values.name !== "" ? values.name : values.legal_company_name,
              surname: values.surname,
              country: values.country,
              region: values.region,
              city: values.city,
              postal_code: values.postal_code,
              address: values.address,
              profession: values.profession,
              passport: values.passport,
              social_security_number: values.social_security_number,
              social_security_series: values.social_security_series,
              mobile_phone_number: values.mobile_phone_number,
              land_phone_number: values.land_phone_number,
              personal_email: values.personal_email,
              business_email: values.business_email,
              legal_company_name: values.legal_company_name,
              legal_fiscal_code: values.legal_fiscal_code,
              legal_unique_registration_code:
                values.legal_unique_registration_code,
              legal_trade_register_number: values.legal_trade_register_number,
              legal_vat_payer: values.legal_vat_payer,
              legal_country: values.legal_country,
              legal_region: values.legal_region,
              legal_city: values.legal_city,
              legal_office_address: values.legal_office_address,
              iban_ron: values.iban_ron,
              iban_euro: values.iban_euro,
              additional_contact1: secondClientId,
              additional_contact2_id: null,
              active: values.active,
            };

            dispatch(onAddNewClient(primaryClient))
              .then(() => {
                handleCloseModal();
              })
              .catch((error) => {
                console.error("Error creating primary client:", error);
              });
          })
          .catch((error) => {
            console.error("Error creating second client:", error);
          });
      } else {
        const newClient = {
          buyer_type: values.buyer_type,
          name: values.name !== "" ? values.name : values.legal_company_name,
          surname: values.surname,
          country: values.country,
          region: values.region,
          city: values.city,
          postal_code: values.postal_code,
          address: values.address,
          profession: values.profession,
          passport: values.passport,
          social_security_number: values.social_security_number,
          social_security_series: values.social_security_series,
          mobile_phone_number: values.mobile_phone_number,
          land_phone_number: values.land_phone_number,
          personal_email: values.personal_email,
          business_email: values.business_email,
          legal_company_name: values.legal_company_name,
          legal_fiscal_code: values.legal_fiscal_code,
          legal_unique_registration_code: values.legal_unique_registration_code,
          legal_trade_register_number: values.legal_trade_register_number,
          legal_vat_payer: values.legal_vat_payer,
          legal_country: values.legal_country,
          legal_region: values.legal_region,
          legal_city: values.legal_city,
          legal_office_address: values.legal_office_address,
          iban_ron: values.iban_ron,
          iban_euro: values.iban_euro,
          additional_contact1: null,
          additional_contact2_id: null,
          active: values.active,
        };

        dispatch(onAddNewClient(newClient))
          .then(() => {
            handleCloseModal();
          })
          .catch((error) => {
            console.error("Error in client creation:", error);
          });
      }
    },
  });

  async function handleNext() {
    validation.validateForm().then((errors) => {
      if (Object.keys(errors).length === 0) {
        toggleArrowTab(activeArrowTab + 1);
      } else {
        validation.setTouched(
          Object.keys(validation.values).reduce((acc, key) => {
            acc[key] = true;
            return acc;
          }, {}),
          true
        );
      }
    });
  }

  return (
    <div>
      <Modal
        isOpen={show}
        toggle={onCloseClick}
        centered={true}
        style={{
          maxWidth: "80%",
          width: "80%",
          backgroundColor: "transparent",
          border: "none",
          boxShadow: "none",
          "--vz-modal-bg": "transparent",
          "--vz-modal-border-color": "transparent",
        }}
      >
        <Form
          className="tablelist-form"
          onSubmit={(e) => {
            if (activeArrowTab === 3) {
              e.preventDefault();
              validation.handleSubmit();
            } else {
              e.preventDefault();
              e.stopPropagation();
            }
            return false;
          }}
        >
          <ModalBody>
            <div className="page-content">
              <Row>
                <Card>
                  <CardHeader>
                    <div className="hstack gap-2 justify-content-end">
                      <Button
                        className="btn btn-light"
                        color="secondary"
                        onClick={() => {
                          handleCloseModal();
                        }}
                      >
                        <i className="ri-close-line"></i>
                      </Button>
                    </div>
                  </CardHeader>
                  <CardBody className="form-steps">
                    <div className="step-arrow-nav mb-4">
                      <Nav
                        className="nav-pills custom-nav nav-justified"
                        role="tablist"
                      >
                        <NavItem>
                          <NavLink
                            id="steparrow-gen-info-tab"
                            className={classnames({
                              active: activeArrowTab === 1,
                              done: activeArrowTab <= 3 && activeArrowTab > 0,
                            })}
                          >
                            {isNaturalClient
                              ? "Client Details"
                              : "Company Details"}
                          </NavLink>
                        </NavItem>

                        <NavItem>
                          <NavLink
                            id="steparrow-gen-info-tab"
                            className={classnames({
                              active: activeArrowTab === 2,
                              done: activeArrowTab <= 3 && activeArrowTab > 1,
                            })}
                          >
                            {isNaturalClient
                              ? "Client Location"
                              : "Company Location"}
                          </NavLink>
                        </NavItem>

                        <NavItem>
                          <NavLink
                            id="steparrow-gen-info-tab"
                            className={classnames({
                              active: activeArrowTab === 3,
                              done: activeArrowTab <= 3 && activeArrowTab > 3,
                            })}
                          >
                            {isNaturalClient
                              ? "Client Accounts"
                              : "Company Accounts"}
                          </NavLink>
                        </NavItem>
                      </Nav>
                    </div>

                    <TabContent activeTab={activeArrowTab}>
                      {isNaturalClient ? (
                        <NaturalClientForm
                          activeArrowTab={activeArrowTab}
                          validation={validation}
                          handleNext={handleNext}
                          toggleArrowTab={toggleArrowTab}
                        />
                      ) : (
                        <LegalClientForm
                          activeArrowTab={activeArrowTab}
                          validation={validation}
                          handleNext={handleNext}
                          toggleArrowTab={toggleArrowTab}
                        />
                      )}
                    </TabContent>
                  </CardBody>
                </Card>
              </Row>
            </div>
          </ModalBody>
        </Form>
      </Modal>
    </div>
  );
};

ClientModal.propTypes = {
  onCloseClick: PropTypes.func,
  show: PropTypes.any,
  clientOption: PropTypes.string,
};

export default ClientModal;
